import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  GripVertical, 
  Trash2, 
  Plus, 
  Minus,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface QuestionBuilderProps {
  question: any;
  index: number;
  onUpdate: (question: any) => void;
  onDelete: () => void;
  questionTypes: { value: string; label: string }[];
}

export function QuestionBuilder({ 
  question, 
  index, 
  onUpdate, 
  onDelete, 
  questionTypes 
}: QuestionBuilderProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const updateQuestion = (field: string, value: any) => {
    onUpdate({
      ...question,
      [field]: value
    });
  };

  const updateOptions = (field: string, value: any) => {
    onUpdate({
      ...question,
      options: {
        ...question.options,
        [field]: value
      }
    });
  };

  const addChoice = () => {
    const choices = question.options.choices || [];
    updateOptions('choices', [...choices, '']);
  };

  const updateChoice = (choiceIndex: number, value: string) => {
    const choices = [...(question.options.choices || [])];
    choices[choiceIndex] = value;
    updateOptions('choices', choices);
  };

  const removeChoice = (choiceIndex: number) => {
    const choices = question.options.choices || [];
    updateOptions('choices', choices.filter((_, i) => i !== choiceIndex));
  };

  const renderQuestionTypeOptions = () => {
    switch (question.type) {
      case 'rating':
        return (
          <div className="space-y-4">
            <div>
              <Label>Rating Scale</Label>
              <Select
                value={question.options.scale?.toString() || '5'}
                onValueChange={(value) => updateOptions('scale', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">1-3 Scale</SelectItem>
                  <SelectItem value="5">1-5 Scale</SelectItem>
                  <SelectItem value="7">1-7 Scale</SelectItem>
                  <SelectItem value="10">1-10 Scale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Scale Labels (optional)</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Low end label (e.g., Poor)"
                  value={question.options.labels?.[0] || ''}
                  onChange={(e) => {
                    const labels = [...(question.options.labels || [])];
                    labels[0] = e.target.value;
                    updateOptions('labels', labels);
                  }}
                />
                <Input
                  placeholder="High end label (e.g., Excellent)"
                  value={question.options.labels?.[1] || ''}
                  onChange={(e) => {
                    const labels = [...(question.options.labels || [])];
                    labels[1] = e.target.value;
                    updateOptions('labels', labels);
                  }}
                />
              </div>
            </div>
          </div>
        );

      case 'multiple_choice':
      case 'checkbox':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label>Answer Choices</Label>
              <Button type="button" variant="outline" size="sm" onClick={addChoice}>
                <Plus className="h-4 w-4 mr-1" />
                Add Choice
              </Button>
            </div>
            
            <div className="space-y-2">
              {(question.options.choices || []).map((choice: string, choiceIndex: number) => (
                <div key={choiceIndex} className="flex gap-2">
                  <Input
                    placeholder={`Choice ${choiceIndex + 1}`}
                    value={choice}
                    onChange={(e) => updateChoice(choiceIndex, e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeChoice(choiceIndex)}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            
            {question.type === 'multiple_choice' && (
              <div className="flex items-center space-x-2">
                <Switch
                  checked={question.options.allow_other || false}
                  onCheckedChange={(checked) => updateOptions('allow_other', checked)}
                />
                <Label>Allow "Other" option</Label>
              </div>
            )}
          </div>
        );

      case 'likert':
        return (
          <div className="space-y-4">
            <div>
              <Label>Likert Scale Type</Label>
              <Select
                value={question.options.likert_type || 'agreement'}
                onValueChange={(value) => updateOptions('likert_type', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agreement">Agreement (Strongly Disagree - Strongly Agree)</SelectItem>
                  <SelectItem value="frequency">Frequency (Never - Always)</SelectItem>
                  <SelectItem value="satisfaction">Satisfaction (Very Dissatisfied - Very Satisfied)</SelectItem>
                  <SelectItem value="importance">Importance (Not Important - Very Important)</SelectItem>
                  <SelectItem value="quality">Quality (Very Poor - Excellent)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Number of Points</Label>
              <Select
                value={question.options.points?.toString() || '5'}
                onValueChange={(value) => updateOptions('points', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 Point Scale</SelectItem>
                  <SelectItem value="4">4 Point Scale</SelectItem>
                  <SelectItem value="5">5 Point Scale</SelectItem>
                  <SelectItem value="7">7 Point Scale</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'text':
      case 'textarea':
        return (
          <div className="space-y-4">
            <div>
              <Label>Placeholder Text</Label>
              <Input
                placeholder="Enter placeholder text"
                value={question.options.placeholder || ''}
                onChange={(e) => updateOptions('placeholder', e.target.value)}
              />
            </div>
            
            {question.type === 'text' && (
              <div>
                <Label>Character Limit</Label>
                <Input
                  type="number"
                  placeholder="Maximum characters (optional)"
                  value={question.options.max_length || ''}
                  onChange={(e) => updateOptions('max_length', e.target.value ? parseInt(e.target.value) : null)}
                />
              </div>
            )}
            
            {question.type === 'textarea' && (
              <div>
                <Label>Number of Rows</Label>
                <Select
                  value={question.options.rows?.toString() || '3'}
                  onValueChange={(value) => updateOptions('rows', parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 rows</SelectItem>
                    <SelectItem value="3">3 rows</SelectItem>
                    <SelectItem value="4">4 rows</SelectItem>
                    <SelectItem value="5">5 rows</SelectItem>
                    <SelectItem value="6">6 rows</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="relative">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
          
          <Badge variant="secondary">Q{index + 1}</Badge>
          
          <div className="flex-1">
            <Select
              value={question.type}
              onValueChange={(value) => updateQuestion('type', value)}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {questionTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Switch
              checked={question.required}
              onCheckedChange={(checked) => updateQuestion('required', checked)}
            />
            <Label className="text-sm">Required</Label>
          </div>
          
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
          
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="pt-0 space-y-4">
          <div>
            <Label>Question Text *</Label>
            <Textarea
              placeholder="Enter your question here..."
              value={question.question}
              onChange={(e) => updateQuestion('question', e.target.value)}
              rows={2}
            />
          </div>
          
          <div>
            <Label>Help Text (optional)</Label>
            <Input
              placeholder="Additional instructions or clarifications"
              value={question.help_text || ''}
              onChange={(e) => updateQuestion('help_text', e.target.value)}
            />
          </div>
          
          {renderQuestionTypeOptions()}
        </CardContent>
      )}
    </Card>
  );
}